import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Form,
  Image,
  InputNumber,
  Popconfirm,
  Result,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from "antd";
import { ArrowRightOutlined, WarningOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

import { isArray } from "lodash";

import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import moment from "moment";

const { Text } = Typography;

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const steps = [
  { title: "Select Pack" },
  { title: "Plan & Confirm" },
  { title: "Verify" },
];

// Tickets sold for a pack — mirrors the backend formula (routes/api.py / slot.py).
function soldTickets(
  isReverse: any,
  start: number,
  current: number,
  isSold: any,
) {
  const s = isSold ? 1 : 0;
  return isReverse ? start - current + s : current - start + s;
}

// Latest report_slot (and its day-close row) for a slot, from the precheck data.
function relatedReport(slotId: any, data: any) {
  const rs = (data?.report_slots ?? [])
    .filter((r: any) => r.slot_id === slotId)
    .sort((a: any, b: any) => b.report_slot_id - a.report_slot_id)[0];
  if (!rs) return {} as any;
  const dayClose = (data?.report_dayclose ?? []).find(
    (d: any) => d.id === rs.report_endshift_id,
  );
  return {
    report_slot_id: rs.report_slot_id,
    report_endshift_id: rs.report_endshift_id,
    dayClose,
  };
}

const ConfigurationPage: React.FC<configurationPageProps> = () => {
  const [form] = Form.useForm();
  const [formRevert] = Form.useForm();

  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);

  const [packData, setPackData] = useState<any>(null);
  const [selectedPack, setSelectedPack] = useState<any>(null);

  const [resetReported, setResetReported] = useState(true);
  const [resolveMove, setResolveMove] = useState(false);
  const [moveToSlot, setMoveToSlot] = useState<number | undefined>(undefined);

  const [salesDelta, setSalesDelta] = useState<number | undefined>(undefined);
  const [revertResult, setRevertResult] = useState<any>(null);
  const [conflict, setConflict] = useState<any>(null);

  const getPackData = (id: any): Promise<any> =>
    queryApi(`slot/pack/${id}`, "GET");

  const revertPack = (id: any, data: any): Promise<any> =>
    queryApi(`slot/revert/${id}`, "PUT", data);

  useEffect(() => {
    form.resetFields();
    formRevert.resetFields();
  }, []);

  const resetAll = () => {
    formRevert.resetFields();
    form.resetFields();
    setPackData(null);
    setSelectedPack(null);
    setResetReported(false);
    setResolveMove(false);
    setMoveToSlot(undefined);
    setSalesDelta(undefined);
    setRevertResult(null);
    setConflict(null);
    setCurrent(0);
  };

  // ---- occupancy state for the selected pack ------------------------------
  const occupancyList: any[] = selectedPack
    ? (packData?.occupancy?.[selectedPack.slot_id] ?? [])
    : [];
  // A conflict only matters when the target is currently sold and we're un-selling it.
  const hasOccupancyConflict =
    !!selectedPack?.is_sold && occupancyList.length > 0;

  // ---- step 1: search a pack ----------------------------------------------
  const onFormSubmit = async (values: any) => {
    setLoading(true);
    try {
      const packs = await getPackData(values.pack_number);
      if (!isArray(packs?.slots) || packs?.slots.length === 0) {
        triggerNotification({
          type: "error",
          title: "No packs found with this number",
          description: "",
        });
        setPackData(null);
      } else {
        setPackData(packs);
      }
    } catch (e: any) {
      triggerNotification({
        type: "error",
        title: "Precheck failed",
        description: e?.message ?? "",
      });
    }
    setLoading(false);
  };

  const selectPack = (record: any) => {
    setSelectedPack(record);
    setConflict(null);
    setResolveMove(false);
    setMoveToSlot(undefined);
    setResetReported(true);
    setSalesDelta(undefined);
    formRevert.setFieldsValue({
      start_no: record.start_no,
      current_no: record.current_no,
    });
    setCurrent(1);
  };

  // ---- step 2: recompute the delta as the form changes --------------------
  const recomputeDelta = (start: any, currentNo: any) => {
    if (start == null || currentNo == null || !selectedPack) {
      setSalesDelta(undefined);
      return;
    }
    const amount = selectedPack?.lottery?.amount ?? 0;
    const oldSold = soldTickets(
      selectedPack.is_reverse,
      selectedPack.start_no,
      selectedPack.current_no,
      selectedPack.is_sold,
    );
    const newSold = soldTickets(selectedPack.is_reverse, start, currentNo, 0);
    setSalesDelta((newSold - oldSold) * amount);
  };

  // ---- step 2: submit the revert ------------------------------------------
  const onRevertFormSubmit = async (values: any) => {
    if (hasOccupancyConflict && !resolveMove) {
      triggerNotification({
        type: "error",
        title: "Occupancy conflict",
        description:
          "Another active pack is on this box. Choose to move the reverted pack to a free box before submitting.",
      });
      return;
    }

    setLoading(true);
    const report = relatedReport(selectedPack.slot_id, packData);
    const oldInstantVal = report?.dayClose?.instant_sales ?? 0;

    // Full SchSlotRevert shape. instant_sales is sent for backward-compat but the
    // server recomputes the correction from sold-ticket deltas and ignores it.
    const body = {
      ...selectedPack,
      start_no: values.start_no,
      current_no: values.current_no,
      report_slot_id: report.report_slot_id,
      report_endshift_id: report.report_endshift_id,
      instant_sales: Math.round(oldInstantVal + (salesDelta ?? 0)),
      reset_is_reported: resetReported,
      resolve_move_to_free: resolveMove,
      move_to_slot_number: resolveMove ? moveToSlot : undefined,
    };

    try {
      const result = await revertPack(selectedPack.slot_id, body);

      if (result?.error_code === "OCCUPANCY_CONFLICT") {
        setConflict(result);
        triggerNotification({
          type: "error",
          title: "Occupancy conflict",
          description: result?.message ?? "",
        });
        setLoading(false);
        return;
      }

      if (result?.status) {
        setRevertResult(result);
        setCurrent(2);
      } else {
        setRevertResult({ status: false, message: result?.message });
        setCurrent(2);
      }
    } catch (e: any) {
      triggerNotification({
        type: "error",
        title: "Revert failed",
        description: e?.message ?? "",
      });
    }
    setLoading(false);
  };

  // ---- table columns for the pack list ------------------------------------
  const columns = [
    {
      title: "Box #",
      dataIndex: "slot_number",
      key: "slot_number",
      sorter: (a: any, b: any) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
      render: (slot_number: any) => <div>#{slot_number}</div>,
    },
    {
      title: "Image",
      dataIndex: "lottery",
      key: "image_url",
      render: (record: any) =>
        record?.image_url ? (
          <Image width={40} height={40} src={record.image_url} />
        ) : (
          "-"
        ),
    },
    {
      title: "Name",
      dataIndex: "lottery",
      key: "name",
      render: (record: any) => record?.name,
    },
    {
      title: "Current #",
      dataIndex: "current_no",
      key: "current_no",
    },
    {
      title: "Start #",
      dataIndex: "start_no",
      key: "start_no",
    },
    {
      title: "Game-Pack #",
      dataIndex: "game",
      key: "game",
      render: (item: any, record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.game}-{record.pack}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status_flags",
      render: (record: any) => (
        <Space size={4} wrap>
          {record.is_sold ? (
            <Tag color="red">SOLD</Tag>
          ) : (
            <Tag color="green">ACTIVE</Tag>
          )}
          {record.is_paused ? <Tag color="orange">PAUSED</Tag> : null}
          {record.is_reported ? <Tag color="blue">REPORTED</Tag> : null}
          {(packData?.occupancy?.[record.slot_id]?.length ?? 0) > 0 ? (
            <Tag icon={<WarningOutlined />} color="volcano">
              BOX CONFLICT
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (item: any) => <div>{moment(item).format("MMM DD, YYYY")}</div>,
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Button
          type="link"
          disabled={packData?.is_allow === false}
          icon={<ArrowRightOutlined />}
          onClick={() => selectPack(record)}
        />
      ),
    },
  ];

  const ledgerColumns = [
    {
      title: "Action",
      dataIndex: "meta_info",
      key: "meta_info",
      render: (item: string) => <Tag>{item}</Tag>,
    },
    { title: "Box #", dataIndex: "slot_number", key: "slot_number" },
    { title: "Scan", dataIndex: "scan_code", key: "scan_code" },
    {
      title: "When",
      dataIndex: "created_at",
      key: "created_at",
      render: (item: any) => moment(item).format("MMM DD, YYYY HH:mm"),
    },
  ];

  const report = selectedPack
    ? relatedReport(selectedPack.slot_id, packData)
    : ({} as any);
  const oldInstant = report?.dayClose?.instant_sales;
  const newInstant =
    oldInstant != null && salesDelta != null
      ? oldInstant + salesDelta
      : undefined;

  return (
    <section id="revert-sold" className="page-section h-page p-6 flex flex-col">
      <div className="mb-4">
        <h2 className="text-2xl font-bold app-text">
          Revert Sold Lottery
        </h2>
        <p className="app-muted mt-1">
          Read-only precheck &rarr; plan the revert &rarr; confirm &rarr;
          transactional apply with a full audit trail.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="app-surface rounded-xl shadow-sm border border-solid app-border p-6">
          <Steps current={current} type="navigation" items={steps} />

          {/* ---------------- STEP 0: SELECT PACK ---------------- */}
          {current === 0 && (
            <div>
              <Form form={form} layout="vertical" onFinish={onFormSubmit}>
                <Form.Item
                  name="pack_number"
                  label="Pack Number"
                  style={{ marginTop: 16 }}
                  rules={[
                    { required: true, message: "Please enter pack number" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter pack number"
                  />
                </Form.Item>
                {packData && packData.is_allow === false && (
                  <Alert
                    message="You are not allowed to revert this pack, contact admin"
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {packData && packData.report_ambiguous && (
                  <Alert
                    message="This pack number spans multiple games / day-closes"
                    description="Pick the exact box row below — each row's report and instant-sales are resolved separately."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Button onClick={form.submit} type="primary" loading={loading}>
                  Search
                </Button>
              </Form>

              <div style={{ overflowY: "auto", marginTop: 16 }}>
                <Table
                  rowKey="slot_id"
                  columns={columns}
                  dataSource={packData?.slots ?? []}
                  sticky
                  tableLayout="auto"
                  size="small"
                  pagination={false}
                />
              </div>

              {isArray(packData?.ledger) && packData.ledger.length > 0 && (
                <>
                  <Divider orientation="left">Recent ledger history</Divider>
                  <Table
                    rowKey="ledger_id"
                    columns={ledgerColumns}
                    dataSource={packData.ledger}
                    size="small"
                    pagination={{ pageSize: 5 }}
                    tableLayout="auto"
                  />
                </>
              )}
            </div>
          )}

          {/* ---------------- STEP 1: PLAN & CONFIRM ---------------- */}
          {current === 1 && selectedPack && (
            <div style={{ marginTop: 16 }}>
              <Card
                size="small"
                title="Selected pack"
                style={{ marginBottom: 16 }}
              >
                <Descriptions size="small" column={2} bordered>
                  <Descriptions.Item label="Game-Pack">
                    {selectedPack.game}-{selectedPack.pack}
                  </Descriptions.Item>
                  <Descriptions.Item label="Box #">
                    #{selectedPack.slot_number}
                  </Descriptions.Item>
                  <Descriptions.Item label="Game">
                    {selectedPack.lottery?.name} ($
                    {selectedPack.lottery?.amount})
                  </Descriptions.Item>
                  <Descriptions.Item label="Direction">
                    {selectedPack.is_reverse ? "Reverse" : "Forward"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Current start / current #">
                    {selectedPack.start_no} / {selectedPack.current_no}
                  </Descriptions.Item>
                  <Descriptions.Item label="Flags">
                    <Space size={4}>
                      {selectedPack.is_sold ? (
                        <Tag color="red">SOLD</Tag>
                      ) : (
                        <Tag color="green">ACTIVE</Tag>
                      )}
                      {selectedPack.is_reported ? (
                        <Tag color="blue">REPORTED</Tag>
                      ) : null}
                      {selectedPack.is_paused ? (
                        <Tag color="orange">PAUSED</Tag>
                      ) : null}
                    </Space>
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* occupancy guardrail */}
              {hasOccupancyConflict && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`Box #${selectedPack.slot_number} already holds another active pack`}
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>
                        Un-selling this pack here would put two active packs on
                        one box (invalid). Conflicting pack(s):{" "}
                        {occupancyList.map((o: any) => (
                          <Tag key={o.slot_id}>pack {o.pack}</Tag>
                        ))}
                      </p>
                      <Checkbox
                        checked={resolveMove}
                        onChange={(e) => setResolveMove(e.target.checked)}
                      >
                        Move the reverted pack to a free box
                      </Checkbox>
                      {resolveMove && (
                        <div style={{ marginTop: 8 }}>
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <InputNumber
                              style={{ width: 200 }}
                              placeholder="Destination box #"
                              value={moveToSlot}
                              onChange={(v) => setMoveToSlot(v as number)}
                            />
                            <Text type="secondary">
                              Pick a free box inside the store's used range so
                              the pack stays visible. In use:{" "}
                              {(
                                packData?.used_slot_numbers?.[
                                  selectedPack?.store_id
                                ] ?? []
                              ).join(", ") || "-"}
                            </Text>
                          </Space>
                        </div>
                      )}
                    </div>
                  }
                />
              )}

              {conflict && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Server blocked the revert (occupancy conflict)"
                  description={conflict?.message}
                />
              )}

              <Form
                form={formRevert}
                layout="vertical"
                onFinish={onRevertFormSubmit}
                onValuesChange={(_, all) =>
                  recomputeDelta(all.start_no, all.current_no)
                }
              >
                <Form.Item
                  name="start_no"
                  label="Start Number"
                  rules={[{ required: true, message: "Please enter start no" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Start no"
                  />
                </Form.Item>

                <Form.Item
                  name="current_no"
                  label="Current Number"
                  rules={[
                    { required: true, message: "Please enter current no" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Current no"
                  />
                </Form.Item>

                <Form.Item>
                  <Checkbox
                    checked={resetReported}
                    onChange={(e) => setResetReported(e.target.checked)}
                  >
                    Reset <code>is_reported</code> &rarr; 0 (re-enter the report
                    cycle)
                  </Checkbox>
                </Form.Item>

                <Card
                  size="small"
                  title="Planned change"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions size="small" column={1}>
                    <Descriptions.Item label="is_sold">
                      {selectedPack.is_sold ? 1 : 0} &rarr; 0
                    </Descriptions.Item>
                    <Descriptions.Item label="Sales delta">
                      <Text type={(salesDelta ?? 0) < 0 ? "danger" : undefined}>
                        {salesDelta != null ? salesDelta : "—"}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Instant sales">
                      {oldInstant != null ? (
                        <span>
                          <span
                            style={{
                              textDecoration: "line-through",
                              marginRight: 6,
                            }}
                          >
                            {oldInstant}
                          </span>
                          {newInstant != null ? newInstant : "—"}
                        </span>
                      ) : (
                        "No matching day-close row"
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Popconfirm
                  title="Apply this revert?"
                  description="This writes to slots, report_slots and report_day_close in one transaction, with a ledger audit row."
                  okText="Apply"
                  onConfirm={() => formRevert.submit()}
                >
                  <Button type="primary" loading={loading}>
                    Review &amp; Apply
                  </Button>
                </Popconfirm>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setSelectedPack(null);
                    setCurrent(0);
                  }}
                >
                  Back
                </Button>
              </Form>
            </div>
          )}

          {/* ---------------- STEP 2: VERIFY ---------------- */}
          {current === 2 && revertResult && (
            <div style={{ marginTop: 16 }}>
              {revertResult.status ? (
                <Result
                  status={
                    revertResult.occupancy_conflict ? "warning" : "success"
                  }
                  title={`Reverted pack ${selectedPack?.pack}`}
                  subTitle={
                    revertResult.occupancy_conflict
                      ? "Applied, but the box still has more than one active pack — resolve it."
                      : "Applied successfully. Box holds exactly one active pack."
                  }
                  extra={[
                    <Card
                      key="details"
                      size="small"
                      title="Final values"
                      style={{ textAlign: "left", marginBottom: 16 }}
                    >
                      <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Slot start / current #">
                          {revertResult.slot?.start_no} /{" "}
                          {revertResult.slot?.current_no}
                        </Descriptions.Item>
                        <Descriptions.Item label="Slot is_sold">
                          {revertResult.slot?.is_sold}
                        </Descriptions.Item>
                        <Descriptions.Item label="Box #">
                          #{revertResult.slot?.slot_number}
                        </Descriptions.Item>
                        <Descriptions.Item label="Sales delta applied">
                          {revertResult.sales_delta} (
                          {revertResult.old_sold_tickets} &rarr;{" "}
                          {revertResult.new_sold_tickets} tickets)
                        </Descriptions.Item>
                        <Descriptions.Item label="Instant sales now">
                          {revertResult.report_day_close?.instant_sales ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Occupancy">
                          {revertResult.occupancy_conflict ? (
                            <Tag color="volcano">
                              {revertResult.occupancy_remaining?.length + 1}{" "}
                              active packs on box — fix required
                            </Tag>
                          ) : (
                            <Tag color="green">Exactly one active pack</Tag>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>,
                    <Button type="primary" key="done" onClick={resetAll}>
                      Done
                    </Button>,
                  ]}
                />
              ) : (
                <Result
                  status="error"
                  title="Revert failed"
                  subTitle={revertResult.message}
                  extra={
                    <Button
                      type="primary"
                      onClick={() => {
                        setRevertResult(null);
                        setCurrent(1);
                      }}
                    >
                      Try Again
                    </Button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  storeId: state.user?.profileData?.storeId,
});

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConfigurationPage);
